import React, { useState, useEffect } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Typography, Spin, Divider, Menu, Tabs, Button } from 'antd';
import { getCourseOfferById } from '../../services/courseOfferService';
import { getCourse } from '../../services/courseService';
import ReadDataTable from '../Common/ReadDataTable';
import UnitOfferSection from '../unitOffer/UnitOfferSection';
import ActionHeader from '../Common/ActionHeader';
import DeleteWarningModal from '../Common/DeleteWarningModal';
import CourseOfferEdit from '../courseOffer/CourseOfferEdit';
import CourseEnrolmentSection from '../courseEnrolment/CourseEnrolmentSection';
import moment from 'moment';
import '../Common/Form.css';
import {
  COHORT_OFFER_TYPES,
  ONDEMAND_COURSE_OFFER_TYPES
} from '../../utilities/constants';

const { Title } = Typography;
const { TabPane } = Tabs;

const CourseOfferIndividual = (props) => {
  const { id } = useParams();
  const [courseOffer, setCourseOffer] = useState({});
  const [formattedData, setFormattedData] = useState({
    general: [],
    details: [],
    trainers: [],
    costs: []
  });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isPricingModalVisible, setIsPricingModalVisible] = useState(
    false
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isCohortCourse, setIsCohortCourse] = useState(false);
  const [course, setCourse] = useState({});
  const [status, setStatus] = useState({
    error: false,
    message: ''
  });

  const renderLivesessions = (liveSessions, type, date) => {
    if (liveSessions)
      return liveSessions.map((item) => {
        if (date == 'start' && item.type == type) {
          return (
            <Button shape="round" type="ghost" style={{ margin: '3px' }}>
              {`${'startDate'}: ${item.startTime}`}
            </Button>
          );
        } else if (date == 'end' && item.type == type) {
          return (
            <Button shape="round" type="ghost" style={{ margin: '3px' }}>
              {`${'endDate'}: ${item.endTime}`}
            </Button>
          );
        }
      });
  };

  const dateFormat = 'dddd, Do MMMM YYYY h:mm:ssA Z';

  const formatData = (data) => {
    return {
      general: [
        {
          label: 'Course*:',
          text: data.courseCode + ' - ' + data.courseDescription
        },
        { label: 'Code*:', text: data.code },
        { label: 'Description:', text: data.description ?? '' },
        { label: 'Status:', text: data.courseOfferStatus ?? '' },
        { label: 'Wave:', text: data.wave ?? '' },
        { label: 'Group Number:', text: data.groupNumber ?? '' },
        {
          label: 'Start Date:',
          text: data.startDate
            ? moment(data.startDate).utc().local().format(dateFormat)
            : '',
          key: 'startDate'
        },
        {
          label: 'End Date:',
          text: data.endDate
            ? moment(data.endDate).utc().local().format(dateFormat)
            : '',
          key: 'endDate'
        },
        { label: 'Frequency:', text: data?.frequency?.join(' & ') ?? '' },
        { label: 'Max Registrations:', text: data.maxRegistrations ?? '' },
        { label: 'Min Registrations::', text: data.minRegistrations ?? '' }
      ],
      trainers: data.trainers,
      details: [
        { label: 'Location:', text: data.location ?? '' },
        { label: 'Zoom Link:', text: data.whereToGo ?? '', link: true },
        {
          label: 'Calendar Link:',
          text: data.calendarLink ?? '',
          link: true
        },
        {
          label: 'Homework Link:',
          text: data.whatToBring ?? '',
          link: true
        },
        {
          label: 'Class Material Link:',
          text: data.classMaterialsLink ?? '',
          link: true
        },
        {
          label:
            data.whatsappGroupLinks?.length > 1
              ? 'Whatsapp Group Links:'
              : 'Whatsapp Group Link:',
          text: data.whatsappGroupLinks?.length
            ? data.whatsappGroupLinks?.join(', ')
            : null,
          link: false
        }
      ],
      costs: [
        { label: 'Total:', text: data.pricingOptions?.total ?? '' },
        {
          label: 'Deposit:',
          text: data.pricingOptions?.deposit?.value ?? ''
        },
        {
          label: 'Deposit Type:',
          text: data.pricingOptions?.deposit?.type ?? ''
        }
      ],
      pricePoints: data.pricingOptions?.pricePoints
    };
  };

  const formatTrainerItems = (list) => {
    list.trainers = list.trainers?.map((item) => {
      if (item)
        return (
          <Link to={'/trainer/' + item.trainerId}>
            <Button shape="round" type="ghost" style={{ margin: '3px' }}>
              {item.firstName + ' ' + item.lastName}
            </Button>
          </Link>
        );
    });
    return list;
  };

  const onCancelModal = () => {
    setIsEditModalVisible(false);
    setIsPricingModalVisible(false);
  };

  const actionMenu = (
    <Menu>
      <Menu.Item onClick={() => setIsEditModalVisible(true)}>
        Edit
      </Menu.Item>
      <Menu.Item onClick={() => setIsDeleteModalVisible(true)}>
        Delete
      </Menu.Item>
      <Menu.Item>
        <Link
          to={{ pathname: `/course-offer/${id}/clone` }}
          target="_blank"
          rel="noopener noreferrer">
          Clone
        </Link>
      </Menu.Item>
    </Menu>
  );

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    setIsCohortCourse(COHORT_OFFER_TYPES.includes(courseOffer.type));
  }, [courseOffer]);

  const fetchData = async () => {
    setIsLoading(true);
    await getCourseOfferById(id)
      .then((response) => {
        if (response.data.length !== 0) {
          let data = formatData(response.data);
          data = formatTrainerItems(data);
          setCourseOffer({
            ...response.data,
            startDate: moment(response.data.startDate)
              .utc()
              .local()
              .format(dateFormat),
            endDate: moment(response.data.endDate)
              .utc()
              .local()
              .format(dateFormat)
          });
          setFormattedData(data);
          getCourse({
            courseId: response.data.courseId
          }).then((response) => {
            setCourse(response.data.course);
          });
        } else {
          throw Error('This course offer does not exist!');
        }
      })
      .catch((err) => {
        setStatus({
          error: true,
          message: `${err}`
        });
      });
    setIsLoading(false);
  };

  function updateCourseOffer(data) {
    setCourseOffer(() => {
      return { ...data };
    });
    let newData = formatData(data);
    newData = formatTrainerItems(newData);
    setFormattedData(newData);
  }

  const renderPricePoints = (pricePoints) => {
    return pricePoints?.map((point, index) => {
      const items = [
        {
          label: 'Number of student:',
          text: point?.number ?? ''
        },
        { label: 'Discount value:', text: point?.discountValue ?? '' },
        { label: 'Type:', text: point?.type ?? '' }
      ];
      return (
        <div>
          <div>
            <b>{index + 1}.</b>
          </div>
          <ReadDataTable key={index} items={items} />
        </div>
      );
    });
  };

  return (
    <div>
      {isLoading ? (
        <div
          style={{
            textAlign: 'center',
            marginTop: '50px'
          }}>
          <Spin />
        </div>
      ) : status.error ? (
        <div style={{ marginTop: '50px', textAlign: 'center' }}>
          <Title>{status.message}</Title>
        </div>
      ) : (
        <>
          <DeleteWarningModal
            isVisible={isDeleteModalVisible}
            setIsVisible={setIsDeleteModalVisible}
            deleteLink={`${process.env.REACT_APP_BACKEND_URL}/bulk-course-offer/${courseOffer.courseOfferId}`}
            params={{}}
          />

          <CourseOfferEdit
            isOpen={isEditModalVisible}
            isCohortCourseEditMode={isCohortCourse}
            onCancelModal={onCancelModal}
            courseOffer={courseOffer}
            course={course}
            updateCourseOffer={updateCourseOffer}
            setIsPricingModalVisible={setIsPricingModalVisible}
            isPricingModalVisible={isPricingModalVisible}
          />

          <ActionHeader
            title={
              'Course Offer ' +
              courseOffer.code +
              ': ' +
              courseOffer.description
            }
            actionMenu={actionMenu}
          />
          <Divider />
          <section className="grey-section">
            <ReadDataTable items={formattedData.general} />

            <Divider />
            <Title level={5}>Trainers</Title>
            <div style={{ padding: '10px' }}>
              {formattedData.trainers ? formattedData.trainers : '--'}
            </div>

            <Divider />
            <Title level={5}>Details</Title>
            <ReadDataTable items={formattedData.details} />

            <Divider />
            {ONDEMAND_COURSE_OFFER_TYPES.includes(courseOffer.type) &&
            courseOffer.liveSessions ? (
              <div>
                <Title level={5}>Live QnA Sessions</Title>
                {renderLivesessions(
                  courseOffer.liveSessions,
                  'qna',
                  'start'
                )}
                {renderLivesessions(
                  courseOffer.liveSessions,
                  'qna',
                  'end'
                )}
              </div>
            ) : null}
            {ONDEMAND_COURSE_OFFER_TYPES.includes(courseOffer.type) &&
            courseOffer.liveSessions ? (
              <div>
                <Title level={5}>Graduation Sessions</Title>
                {renderLivesessions(
                  courseOffer.liveSessions,
                  'graduation',
                  'start'
                )}
                {renderLivesessions(
                  courseOffer.liveSessions,
                  'graduation',
                  'end'
                )}
              </div>
            ) : null}
            <Divider />
            <Title level={5}>Price</Title>
            <ReadDataTable items={formattedData.costs} />
            <Title level={5}>Price points</Title>
            {renderPricePoints(formattedData.pricePoints)}

            <Divider />
            <Title level={5}>Notes</Title>
            <div style={{ padding: '10px' }}>
              {courseOffer.generalNotes}
            </div>
          </section>
          <br />
          <Tabs defaultActiveKey="1" type="card" size="middle">
            <TabPane tab="Unit Offers" key="1">
              <UnitOfferSection />
            </TabPane>
            <TabPane tab="Course Enrolments" key="2">
              <CourseEnrolmentSection courseOffer={courseOffer} />
            </TabPane>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default withRouter(CourseOfferIndividual);
